import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { DailyCalendar, DailyCheckinView, WeeklyCheckinView, MonthlyReviewView } from './BusinessReviewHub';
import ProjectViewModal from '../../components/workspace/project/ProjectViewModal';
import { QueriesPanel } from '../../components/workspace/review/ReviewQueries';
import '../../styles/MyDashboard.css';
import '../../styles/BusinessReviews.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function displayName(user) {
  if (!user) return 'You';
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || 'You';
}

function firstName(user) {
  return user?.first_name || displayName(user).split(' ')[0] || 'there';
}

function fmtHours(minutes) {
  return (minutes / 60).toFixed(1);
}

// "125" -> "2h 5m" (matches the planner's slot-duration format)
function fmtHoursMins(minutes) {
  const total = Math.round(minutes || 0);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h)      return `${h}h`;
  return `${m}m`;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

function fmtWeek(ymw) {
  return ymw ? ymw.split('-')[1] : '';
}

function fmtActivityTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yest';
  if (diffDays < 7)  return d.toLocaleDateString(undefined, { weekday: 'short' });
  return fmtDate(ts);
}

function todayEyebrow() {
  const now = new Date();
  const day  = now.toLocaleDateString(undefined, { weekday: 'short' });
  const date = now.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  return `${day} · ${date}`;
}

const TAG_COLORS = {
  hours:   { bg: 'var(--accent-soft)',        color: 'var(--accent)' },
  task:    { bg: 'var(--status-success-bg)',  color: 'var(--pos)' },
  post:    { bg: 'var(--status-info-bg)',     color: 'var(--info)' },
  kudos:   { bg: '#fef3c7',                  color: '#d97706' },
  comment: { bg: 'var(--bg-muted)',           color: 'var(--text-secondary)' },
};

// ── Last 12 months as { val: 'YYYY-MM', lbl: 'Month YYYY' } ──────────────────
function last12MonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const lbl = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    opts.push({ val, lbl });
  }
  return opts;
}

// ── Arrow-only month picker ───────────────────────────────────────────────────
function MonthPickerArrow({ value, onChange, options, active, buttonStyle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`mdb-chart-month-select${active ? ' active' : ''}`}
        style={{ padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 2, ...buttonStyle }}
        title="Select month"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: 160, maxHeight: 240, overflowY: 'auto', marginTop: 4,
        }}>
          {options.map(o => (
            <div
              key={o.val}
              onClick={() => { onChange(o.val); setOpen(false); }}
              style={{
                padding: '8px 14px', fontSize: 12, cursor: 'pointer',
                fontWeight: o.val === value ? 700 : 400,
                color: o.val === value ? 'var(--accent)' : 'var(--text-primary)',
                background: o.val === value ? 'var(--accent-soft, #e0f2fe)' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = o.val === value ? 'var(--accent-soft, #e0f2fe)' : 'transparent'}
            >
              {o.lbl}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SVG Area + Line chart ─────────────────────────────────────────────────────

function HoursAreaChart({ data, labelFn }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const W = 600, H = 160;
  const PAD = { t: 22, r: 16, b: 28, l: 38 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;
  const TARGET_MIN = 8 * 60;
  const maxMin = Math.max(...data.map(d => d.minutes), TARGET_MIN, 1);
  const n = data.length;

  const xOf = i => PAD.l + (n <= 1 ? chartW / 2 : (i / (n - 1)) * chartW);
  const yOf = v => PAD.t + chartH - (v / maxMin) * chartH;

  const pts = data.map((d, i) => [xOf(i), yOf(d.minutes)]);
  const linePath = pts.length
    ? `M${pts[0][0]},${pts[0][1]} ` + pts.slice(1).map(([x, y]) => `L${x},${y}`).join(' ')
    : '';

  const targetY = yOf(TARGET_MIN);
  const peakIdx = data.reduce((best, d, i) => d.minutes > data[best].minutes ? i : best, 0);
  const peakVal = data[peakIdx]?.minutes || 0;
  const peakX   = xOf(peakIdx);
  const peakY   = yOf(peakVal);
  const total   = data.reduce((s, d) => s + d.minutes, 0);
  const nonZero = data.filter(d => d.minutes > 0).length;
  const avg     = nonZero ? total / nonZero : 0;

  const xLabels = [0, Math.floor((n - 1) / 2), n - 1].map(i => ({ i, label: labelFn(data[i]) }));

  const hover = hoverIdx != null ? data[hoverIdx] : null;
  const hoverX = hoverIdx != null ? xOf(hoverIdx) : 0;
  const hoverY = hoverIdx != null ? yOf(hover.minutes) : 0;
  const BUBBLE_W = 66;
  const bubbleX = Math.min(Math.max(hoverX, PAD.l + BUBBLE_W / 2), W - PAD.r - BUBBLE_W / 2);
  // Points near the top of the chart leave no room for a bubble drawn above
  // them — flip it below the dot instead of letting it clip past the SVG's
  // top edge (the root <svg> clips overflow by default).
  const bubbleBelow = hoverY - 36 < 2;

  return (
    <div className="mdb-area-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="mdb-area-chart-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="mdbLineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="var(--accent)" stopOpacity="0.4" />
            <stop offset="50%"  stopColor="var(--accent)" stopOpacity="1"   />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.6" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map(f => (
          <line key={f}
            x1={PAD.l} y1={PAD.t + chartH * (1 - f)}
            x2={W - PAD.r} y2={PAD.t + chartH * (1 - f)}
            stroke="var(--border)" strokeWidth="0.6" strokeDasharray="4,4"
          />
        ))}

        {TARGET_MIN <= maxMin && (
          <line x1={PAD.l} y1={targetY} x2={W - PAD.r} y2={targetY}
            stroke="var(--warn)" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.65" />
        )}

        {linePath && (
          <path d={linePath} fill="none"
            stroke="url(#mdbLineGrad)" strokeWidth="2.2"
            strokeLinejoin="round" strokeLinecap="round" />
        )}

        {pts.map(([x, y], i) =>
          data[i].minutes > 0 && (
            <circle key={i} cx={x} cy={y} r="3.5"
              fill="var(--bg-card)" stroke="var(--accent)" strokeWidth="2" />
          )
        )}

        {peakVal > 0 && hoverIdx !== peakIdx && (
          <g>
            <rect x={peakX - 22} y={peakY - 22} width="44" height="16" rx="4"
              fill="var(--accent)" opacity="0.9" />
            <text x={peakX} y={peakY - 10} textAnchor="middle"
              fontSize="9" fill="#fff" fontWeight="600">
              {fmtHours(peakVal)}h
            </text>
          </g>
        )}

        {/* Invisible per-point hit targets — wider than the visible dot so
            hovering near a day (including zero-hour days, which draw no
            dot at all) still reveals its date and logged hours. */}
        {pts.map(([x, y], i) => (
          <circle key={`hit-${i}`} cx={x} cy={y} r="10" fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
            style={{ cursor: 'pointer' }} />
        ))}

        {hover && (
          <g style={{ pointerEvents: 'none' }}>
            <rect x={bubbleX - BUBBLE_W / 2} y={bubbleBelow ? hoverY + 8 : hoverY - 36} width={BUBBLE_W} height="28" rx="5"
              fill="var(--accent)" opacity="0.94" />
            <text x={bubbleX} y={bubbleBelow ? hoverY + 20 : hoverY - 24} textAnchor="middle"
              fontSize="8.5" fill="#fff" fontWeight="500">
              {labelFn(hover)}
            </text>
            <text x={bubbleX} y={bubbleBelow ? hoverY + 32 : hoverY - 12} textAnchor="middle"
              fontSize="9.5" fill="#fff" fontWeight="700">
              {fmtHoursMins(hover.minutes)}
            </text>
            <circle cx={hoverX} cy={hoverY} r="4" fill="var(--accent)"
              stroke="var(--bg-card)" strokeWidth="1.5" />
          </g>
        )}

        <text x={PAD.l - 5} y={PAD.t + chartH / 2} fontSize="9"
          fill="var(--text-muted)" textAnchor="end" dominantBaseline="middle">
          {fmtHours(maxMin / 2)}h
        </text>

        {xLabels.map(({ i, label }) => (
          <text key={i} x={xOf(i)} y={H - 4} textAnchor="middle"
            fontSize="9" fill="var(--text-muted)">
            {label}
          </text>
        ))}
      </svg>

      <div className="mdb-chart-summary">
        <span><strong>{fmtHours(avg)}h</strong> avg/day</span>
        <span><strong>{fmtHours(peakVal)}h</strong> peak</span>
        <span><strong>{fmtHours(total)}h</strong> total</span>
      </div>
    </div>
  );
}

// ── Sparkline bars ────────────────────────────────────────────────────────────

function Sparkline({ data }) {
  const max = Math.max(...(data || []).map(d => d.minutes), 1);
  return (
    <div className="mdb-sparkline">
      {(data || []).map((d, i) => (
        <div key={i} className="mdb-spark-bar"
          style={{ height: `${Math.max((d.minutes / max) * 100, d.minutes > 0 ? 8 : 0)}%` }} />
      ))}
    </div>
  );
}

// ── Task donut ────────────────────────────────────────────────────────────────

function TaskDonut({ tasks }) {
  const C = 2 * Math.PI * 50;
  const total     = tasks.total || 1;
  const donePct   = tasks.done / total;
  const ipPct     = (tasks.open || 0) / total;
  const overduePct = (tasks.overdue || 0) / total;
  const pct = n => tasks.total > 0 ? `${Math.round((n / tasks.total) * 100)}%` : '—';

  return (
    <div className="mdb-task-body">
      <div className="mdb-ring-wrap">
        <svg className="mdb-ring" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="50" fill="none" stroke="var(--border)" strokeWidth="12" />
          <circle cx="60" cy="60" r="50" fill="none" stroke="#10b981" strokeWidth="12"
            strokeDasharray={`${donePct * C} ${C}`} strokeDashoffset={0} />
          {ipPct > 0 && (
            <circle cx="60" cy="60" r="50" fill="none" stroke="#3b82f6" strokeWidth="12"
              strokeDasharray={`${ipPct * C} ${C}`} strokeDashoffset={-(donePct * C)} />
          )}
          {overduePct > 0 && (
            <circle cx="60" cy="60" r="50" fill="none" stroke="#ef4444" strokeWidth="12"
              strokeDasharray={`${overduePct * C} ${C}`} strokeDashoffset={-(donePct + ipPct) * C} />
          )}
        </svg>
        <div className="mdb-ring-label">
          <span className="mdb-ring-count">{tasks.total}</span>
          <span className="mdb-ring-sub">Total</span>
        </div>
      </div>

      <div className="mdb-task-right">
        <ul className="mdb-task-list">
          <li>
            <span className="mdb-dot" style={{ background: '#10b981' }} />
            <span className="mdb-task-lbl">Completed</span>
            <strong>{tasks.done}</strong>
            <span className="mdb-task-pct">{pct(tasks.done)}</span>
          </li>
          <li>
            <span className="mdb-dot" style={{ background: '#3b82f6' }} />
            <span className="mdb-task-lbl">Not Started</span>
            <strong>{tasks.open || 0}</strong>
            <span className="mdb-task-pct">{pct(tasks.open || 0)}</span>
          </li>
          <li>
            <span className="mdb-dot" style={{ background: tasks.overdue > 0 ? '#ef4444' : '#9ca3af' }} />
            <span className="mdb-task-lbl">Overdue</span>
            <strong style={{ color: tasks.overdue > 0 ? '#ef4444' : undefined }}>{tasks.overdue || 0}</strong>
            <span className="mdb-task-pct" style={{ color: tasks.overdue > 0 ? '#ef4444' : undefined }}>
              {pct(tasks.overdue || 0)}
            </span>
          </li>
          {tasks.blocked > 0 && (
            <li>
              <span className="mdb-dot" style={{ background: '#f59e0b' }} />
              <span className="mdb-task-lbl">Blocked</span>
              <strong>{tasks.blocked}</strong>
              <span className="mdb-task-pct">{pct(tasks.blocked)}</span>
            </li>
          )}
        </ul>

        {tasks.overdue > 0 && (
          <div className="mdb-overdue-banner">
            {tasks.overdue} task{tasks.overdue > 1 ? 's' : ''} overdue
          </div>
        )}
      </div>
    </div>
  );
}

// ── Delta chip ────────────────────────────────────────────────────────────────

function DeltaChip({ pct, label }) {
  if (pct === 0 || pct == null) return null;
  const pos = pct > 0;
  return (
    <span className={`mdb-chip ${pos ? 'pos' : 'neg'}`}>
      {pos ? '↑' : '↓'} {Math.abs(pct)}% {label}
    </span>
  );
}

// ── StarDisplay ───────────────────────────────────────────────────────────────

export function StarDisplay({ value, size = 'md' }) {
  if (!value) return <span className="mdb-no-score">—</span>;
  const full  = Math.floor(value);
  const frac  = value - full;
  const empty = 5 - Math.ceil(value);
  return (
    <span className={`mdb-stars ${size}`}>
      {'★'.repeat(full)}
      {frac >= 0.5 ? '½' : ''}
      {'☆'.repeat(empty)}
      <span className="mdb-star-val">{value.toFixed(1)}</span>
    </span>
  );
}

// ── Engagement Gauge ─────────────────────────────────────────────────────────

function EngagementGauge({ score, target = 70 }) {
  const CX = 110, CY = 90, R = 68;
  const toRad = d => (d * Math.PI) / 180;
  const polar = d => [
    +(CX + R * Math.cos(toRad(d))).toFixed(1),
    +(CY + R * Math.sin(toRad(d))).toFixed(1),
  ];

  const START = 150, TOTAL = 240;
  const [sx, sy] = polar(START);
  const [ex, ey] = polar(30);

  const trackPath = `M ${sx} ${sy} A ${R} ${R} 0 1 1 ${ex} ${ey}`;

  const fillPct = Math.min(score / 100, 1);
  const fillEndDeg = START + fillPct * TOTAL;
  const [fx, fy] = polar(fillEndDeg);
  const fillSpan = fillPct * TOTAL;
  const fillLarge = fillSpan > 180 ? 1 : 0;
  const fillPath = score > 0
    ? `M ${sx} ${sy} A ${R} ${R} 0 ${fillLarge} 1 ${fx} ${fy}`
    : null;

  const scoreColor = score >= 86 ? '#10b981'
    : score >= 70 ? '#3b82f6'
    : score >= 51 ? '#f59e0b'
    : score >= 31 ? '#f97316'
    : '#9ca3af';

  return (
    <svg viewBox="0 0 220 130" className="mdb-eng-gauge-svg">
      <defs>
        <linearGradient id="engGrad" gradientUnits="userSpaceOnUse" x1={sx} y1="0" x2={ex} y2="0">
          <stop offset="0%"   stopColor="#ef4444" />
          <stop offset="35%"  stopColor="#f59e0b" />
          <stop offset="65%"  stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#10b981" />
        </linearGradient>
      </defs>
      <path d={trackPath} fill="none" stroke="var(--border)" strokeWidth="14" strokeLinecap="round" />
      {fillPath && (
        <path d={fillPath} fill="none" stroke="url(#engGrad)" strokeWidth="14" strokeLinecap="round" />
      )}
      <text x={CX} y={CY + 4} textAnchor="middle" fontSize="40" fontWeight="800" fill={scoreColor}>{score}</text>
      <text x={CX} y={CY + 20} textAnchor="middle" fontSize="11" fill="var(--text-muted)">pts</text>
    </svg>
  );
}

// ── Routine Summary ───────────────────────────────────────────────────────────

function MyRoutineSummary({ appId, api }) {
  const defaultMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const [currentMonth, setCurrentMonth] = useState(defaultMonth);
  const monthOpts = last12MonthOptions();
  const isCurrentMonth = currentMonth === defaultMonth;

  const [rData, setRData] = useState(null);
  const [rLoad, setRLoad] = useState(true);
  const [rErr,  setRErr]  = useState(null);
  const [rtView, setRtView] = useState('routines'); // 'routines' | 'areas'

  useEffect(() => {
    setRLoad(true);
    api.getRoutineDashboard(appId, undefined, { mode: 'month', month: currentMonth })
      .then(setRData)
      .catch(() => setRErr(true))
      .finally(() => setRLoad(false));
  }, [appId, currentMonth]);

  const monthNav = (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button
        className={`mdb-chart-tab${!isCurrentMonth ? ' active' : ''}`}
        style={{ borderRadius: '6px 0 0 6px', borderRight: 'none' }}
        onClick={() => setCurrentMonth(defaultMonth)}
      >
        {!isCurrentMonth
          ? monthOpts.find(o => o.val === currentMonth)?.lbl?.replace(/\s\d{4}$/, '') || 'Month'
          : 'This Month'}
      </button>
      <MonthPickerArrow
        value={currentMonth}
        active={!isCurrentMonth}
        options={monthOpts}
        onChange={setCurrentMonth}
        buttonStyle={{ borderRadius: '0 6px 6px 0' }}
      />
    </div>
  );

  if (rLoad) return (
    <div className="mdb-card mdb-routine-card">
      <div className="mdb-routine-loading">Loading routines…</div>
    </div>
  );

  if (rErr || !rData) return (
    <div className="mdb-card mdb-routine-card">
      <div className="mdb-card-head">
        <h2 className="mdb-card-title">My Routines</h2>
        <Link to={`/apps/${appId}/routines`} className="mdb-link-btn">View →</Link>
      </div>
      <div className="mdb-routine-empty">Could not load routine data.</div>
    </div>
  );

  // Quarterly/yearly routines not yet due this month (and not done early) come
  // back as not_applicable — they don't count toward the month at all.
  const stats     = (rData.routine_stats || []).filter(r => r.status !== 'not_applicable');
  const attention = stats.filter(r => ['pending','incomplete','not_started'].includes(r.status));
  const onTrack   = stats.filter(r => r.status === 'completed' || r.status === 'on_track');
  const efficiency = rData.overall_efficiency;
  const effColor   = efficiency >= 80 ? '#10b981' : efficiency >= 50 ? '#f59e0b' : '#ef4444';

  const areaStats = rData.area_stats || [];
  const areaColor = (p) => p == null ? '#9ca3af' : p >= 80 ? '#10b981' : p >= 50 ? '#f59e0b' : '#ef4444';
  const showAreas = rtView === 'areas';
  // Overall area progress = mean of each area's own completion %, across the
  // areas that have at least one task this month. Same per-item-average basis
  // as the Routines "Efficiency" / "% overall" bar (mean of per-routine %).
  // 0 (not "—") when none of this role's areas have any tasks yet — the
  // "no areas at all" case is handled separately by the "No Area Assigned"
  // empty state, so getting here means areas exist and just haven't started.
  const areasWithPct = areaStats.filter(a => a.pct != null);
  const areaOverall = areasWithPct.length
    ? Math.round(areasWithPct.reduce((s, a) => s + a.pct, 0) / areasWithPct.length)
    : 0;
  // On Track = every task created in the area this month is done (e.g. 3/3).
  // Needs Attention = tasks created but not all done (e.g. 2/3), or no tasks
  // at all. Same rule as the Team Areas grid, kept in sync here.
  const areaOnTrack    = areaStats.filter(a => a.total > 0 && a.done >= a.total);
  const areaAttention  = areaStats.filter(a => !areaOnTrack.includes(a));
  const areaNotStarted = [];

  return (
    <div className="mdb-card mdb-routine-card" style={{ flex: 1 }}>
      <div className="mdb-card-head">
        <h2 className="mdb-card-title">{showAreas ? 'My Areas' : 'My Routines'}</h2>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <div className="mdb-rt-tabs">
            <button
              className={`mdb-rt-tab${rtView === 'routines' ? ' active' : ''}`}
              onClick={() => setRtView('routines')}
            >Routines</button>
            <button
              className={`mdb-rt-tab${rtView === 'areas' ? ' active' : ''}`}
              onClick={() => setRtView('areas')}
            >Areas</button>
          </div>
          {monthNav}
          {!showAreas && (
            <Link to={`/apps/${appId}/routines`} className="mdb-link-btn">View all →</Link>
          )}
        </div>
      </div>

      {showAreas ? areaStats.length === 0 ? (
        <div className="mdb-routine-empty">No Area Assigned</div>
      ) : (
        <>
          {/* KPI strip — mirrors the Routines view */}
          <div className="mdb-routine-kpis">
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val" style={{ color: areaColor(areaOverall) }}>
                {areaOverall}%
              </span>
              <span className="mdb-routine-kpi-lbl">Completion</span>
            </div>
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val" style={{ color: '#10b981' }}>{areaOnTrack.length}</span>
              <span className="mdb-routine-kpi-lbl">On Track</span>
            </div>
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val" style={{ color: '#f59e0b' }}>{areaAttention.length}</span>
              <span className="mdb-routine-kpi-lbl">Needs Attention</span>
            </div>
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val">{areaStats.length}</span>
              <span className="mdb-routine-kpi-lbl">Total</span>
            </div>
          </div>

          <div className="mdb-routine-bar-wrap">
            <div className="mdb-routine-bar-track">
              <div className="mdb-routine-bar-fill" style={{ width: `${areaOverall}%`, background: areaColor(areaOverall) }} />
            </div>
            <span className="mdb-routine-bar-label">{areaOverall}% overall</span>
          </div>

          <div className="mdb-routine-area-head">
            <span className="mdb-routine-area-head-name">Area</span>
            <span className="mdb-routine-area-head-col">Time Spent</span>
            <span className="mdb-routine-area-head-col">Tasks</span>
          </div>
          <div className="mdb-routine-scroll">
            {/* On Track */}
            {areaOnTrack.length > 0 && (
              <div className="mdb-routine-section">
                <div className="mdb-routine-section-title mdb-routine-section-ok">✓ On Track</div>
                {areaOnTrack.map(a => (
                  <div key={a.area_id} className="mdb-routine-row">
                    <div className="mdb-routine-dot" style={{ background: areaColor(a.pct) }} />
                    <div className="mdb-routine-row-name">{a.area_name}</div>
                    <div className="mdb-routine-row-meta">{a.minutes > 0 ? fmtHoursMins(a.minutes) : '—'}</div>
                    <div className="mdb-routine-row-meta">{a.done} / {a.total}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Needs Attention */}
            {areaAttention.length > 0 && (
              <div className="mdb-routine-section">
                <div className="mdb-routine-section-title mdb-routine-section-warn">⚠ Needs Attention</div>
                {areaAttention.map(a => (
                  <div key={a.area_id} className="mdb-routine-row">
                    <div className="mdb-routine-dot" style={{ background: areaColor(a.pct) }} />
                    <div className="mdb-routine-row-name">{a.area_name}</div>
                    <div className="mdb-routine-row-meta">{a.minutes > 0 ? fmtHoursMins(a.minutes) : '—'}</div>
                    <div className="mdb-routine-row-meta">{a.done} / {a.total}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Not Started */}
            {areaNotStarted.length > 0 && (
              <div className="mdb-routine-section">
                <div className="mdb-routine-section-title mdb-routine-section-muted">Not Started</div>
                {areaNotStarted.map(a => (
                  <div key={a.area_id} className="mdb-routine-row">
                    <div className="mdb-routine-dot" style={{ background: areaColor(a.pct) }} />
                    <div className="mdb-routine-row-name">{a.area_name}</div>
                    <div className="mdb-routine-row-meta">—</div>
                    <div className="mdb-routine-row-meta">—</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : stats.length === 0 ? (
        <div className="mdb-routine-empty">No routines assigned for this month.</div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="mdb-routine-kpis">
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val" style={{ color: effColor }}>
                {efficiency != null ? `${efficiency}%` : '—'}
              </span>
              <span className="mdb-routine-kpi-lbl">Efficiency</span>
            </div>
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val" style={{ color: '#10b981' }}>{onTrack.length}</span>
              <span className="mdb-routine-kpi-lbl">On Track</span>
            </div>
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val" style={{ color: '#f59e0b' }}>{attention.length}</span>
              <span className="mdb-routine-kpi-lbl">Needs Attention</span>
            </div>
            <div className="mdb-routine-kpi">
              <span className="mdb-routine-kpi-val">{stats.length}</span>
              <span className="mdb-routine-kpi-lbl">Total</span>
            </div>
          </div>

          {efficiency != null && (
            <div className="mdb-routine-bar-wrap">
              <div className="mdb-routine-bar-track">
                <div className="mdb-routine-bar-fill" style={{ width: `${efficiency}%`, background: effColor }} />
              </div>
              <span className="mdb-routine-bar-label">{efficiency}% overall</span>
            </div>
          )}

          <div className="mdb-routine-scroll">
            {/* Needs Attention */}
            {attention.length > 0 && (
              <div className="mdb-routine-section">
                <div className="mdb-routine-section-title mdb-routine-section-warn">⚠ Needs Attention</div>
                {attention.map(r => (
                  <div key={r.routine.id} className="mdb-routine-row">
                    <div className="mdb-routine-dot" style={{ background: r.status === 'incomplete' ? '#ef4444' : '#f59e0b' }} />
                    <div className="mdb-routine-row-name">{r.routine.description}</div>
                    <div className="mdb-routine-row-meta">
                      {r.expected_count > 0 ? `${r.tasks_completed}/${r.expected_count}` : `${r.tasks_completed} done`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* On Track */}
            {onTrack.length > 0 && (
              <div className="mdb-routine-section">
                <div className="mdb-routine-section-title mdb-routine-section-ok">✓ On Track</div>
                {onTrack.map(r => (
                  <div key={r.routine.id} className="mdb-routine-row">
                    <div className="mdb-routine-dot" style={{ background: '#10b981' }} />
                    <div className="mdb-routine-row-name">{r.routine.description}</div>
                    <div className="mdb-routine-row-meta">
                      {r.expected_count > 0 ? `${r.tasks_completed}/${r.expected_count}` : `${r.tasks_completed} done`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── My Projects Section ───────────────────────────────────────────────────────

const STATUS_COLOR = {
  completed: '#22c55e',
  overdue:   '#ef4444',
  near_end:  '#f59e0b',
  active:    '#3b82f6',
  on_hold:   '#9ca3af',
};
const STATUS_LABEL = {
  completed: 'Completed',
  overdue:   'Overdue',
  near_end:  'Near End',
  active:    'Active',
  on_hold:   'On Hold',
};

function MyProjectsSection({ appId, me }) {
  const api = useApi();
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [viewProject, setViewProject] = useState(null);
  const [members, setMembers]     = useState([]);
  const [roles, setRoles]         = useState([]);
  const [areasByRole, setAreasByRole] = useState(new Map());
  const modalDataLoadedRef = useRef(false);

  useEffect(() => {
    api.listProjects(appId, { mine: true })
      .then(d => setProjects(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [appId]);

  function openProject(p) {
    setViewProject(p);
    if (modalDataLoadedRef.current) return;
    modalDataLoadedRef.current = true;
    Promise.all([
      api.listAppMembers(appId),
      api.roles.list(appId).catch(() => ({ items: [] })),
    ]).then(([mRes, rRes]) => {
      const roleRows = rRes?.items || [];
      setMembers(mRes?.items || []);
      setRoles(roleRows);
      const aMap = new Map();
      return Promise.all(roleRows.map(r =>
        api.listAreas(appId, r.id)
          .then(res => aMap.set(r.id, res?.items || []))
          .catch(() => aMap.set(r.id, []))
      )).then(() => setAreasByRole(aMap));
    }).catch(() => { modalDataLoadedRef.current = false; });
  }

  if (loading) return null;
  if (!projects.length) return null;

  const myProjects  = projects.filter(p => p.owner_user_id === me?.id);
  const otherProjects = projects.filter(p => p.owner_user_id !== me?.id);
  const sorted = [...myProjects, ...otherProjects];

  return (
    <>
      <div className="mdb-card mdb-projects-card">
        <div className="mdb-card-head">
          <h2 className="mdb-card-title">My Projects</h2>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sorted.length} project{sorted.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="mdb-projects-list">
          {sorted.map(p => {
            const color = STATUS_COLOR[p.computed_status] || '#3b82f6';
            const label = STATUS_LABEL[p.computed_status] || (p.computed_status || 'Active');
            const pct   = p.completion_pct ?? 0;
            const isOwner = p.owner_user_id === me?.id;
            return (
              <div key={p.id} className="mdb-project-item" onClick={() => openProject(p)}>
                <div className="mdb-project-header">
                  <span className="mdb-project-name">{p.name}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isOwner && (
                      <span className="mdb-project-owner-badge">Owner</span>
                    )}
                    <span className="mdb-project-status-chip" style={{ background: `${color}22`, color }}>
                      {label}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 12, color }}>{pct}%</span>
                  </span>
                </div>
                <div className="mdb-project-bar-bg">
                  <div className="mdb-project-bar-fill" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {viewProject && (
        <ProjectViewModal
          open={!!viewProject}
          onClose={() => setViewProject(null)}
          project={viewProject}
          members={members}
          roles={roles}
          areasByRole={areasByRole}
          onUpdate={() => {
            api.listProjects(appId, { mine: true }).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {});
          }}
        />
      )}
    </>
  );
}

// ── Daily Calendar Widget (calendar only, no stats/list) ─────────────────────

function DailyCalendarWidget({ appId, onOpenReview }) {
  const api = useApi();
  const [dailyReviews, setDailyReviews] = useState([]);
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const to = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
    // My Dashboard shows the person's OWN reviews — the ones where they are the
    // reportee/attendee, never the ones they run as a reviewer/manager (those
    // live on the Manager Dashboard's Team Review Calendar and the BR Hub).
    api.listBusinessReviews(appId, { type: 'daily', from, to, per_page: 100, attendee_only: 'true' })
      .then(res => setDailyReviews(res.reviews || []))
      .catch(() => setDailyReviews([]));
  }, [appId, month]);

  const [y, m] = month.split('-').map(Number);
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const shiftMonth = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '6px 16px 4px' }}>
        <button
          onClick={() => shiftMonth(-1)}
          style={{ border: 'none', background: 'none', padding: '2px 6px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 15, lineHeight: 1 }}
        >&#x2039;</button>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)' }}>{monthLabel}</span>
        <button
          onClick={() => shiftMonth(1)}
          style={{ border: 'none', background: 'none', padding: '2px 6px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 15, lineHeight: 1 }}
        >&#x203a;</button>
      </div>
      <DailyCalendar
        month={month}
        dailyReviews={dailyReviews}
        onDayClick={(id, type) => onOpenReview({ id, type })}
        onCreateForDate={() => {}}
        canCreate={false}
      />
    </div>
  );
}

// ── My Criteria Breakdown (collapsible) ──────────────────────────────────────

function MyCriteriaModal({ categories, total, level, onClose }) {
  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f97316', '#06b6d4'];
  return (
    <div className="mdb-modal-overlay" onClick={onClose}>
      <div className="mdb-modal-box" onClick={e => e.stopPropagation()}>
        <div className="mdb-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🏆</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>My GLOS Score Breakdown</div>
              <div style={{ fontSize: 12, color: level.color, fontWeight: 700 }}>{level.icon} {level.label} · {total} pts total</div>
            </div>
          </div>
          <button className="mdb-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mdb-modal-body">
          {categories.map((cat, i) => {
            const color = palette[i % palette.length];
            const barPct = cat.max != null ? Math.min((cat.score / cat.max) * 100, 100)
              : total > 0 ? Math.min((cat.score / total) * 100, 100) : 0;
            return (
              <div key={cat.key} className="mdb-lb-criteria-row" style={{ marginBottom: 14 }}>
                <div className="mdb-lb-criteria-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>{cat.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color }}>{cat.score} pts</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', margin: '6px 0 4px' }}>
                  <div style={{ height: '100%', width: `${barPct}%`, background: color, borderRadius: 3 }} />
                </div>
                {cat.detail && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{cat.detail}</div>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', textAlign: 'right', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            Total: {total} pts
          </div>
        </div>
      </div>
    </div>
  );
}

function MyCriteriaBadges({ categories, total, level }) {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <button className="mdb-lb-view-btn" style={{ marginBottom: 10 }} onClick={() => setModalOpen(true)}>
        View my score ▸
      </button>
      {modalOpen && (
        <MyCriteriaModal categories={categories} total={total} level={level} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MyDashboard() {
  const { appId } = useParams();
  const api = useApi();
  const { me, app, myRole } = useWorkspace();
  const { user: clerkUser } = useUser();
  const navigate = useNavigate();

  const [data, setData]                     = useState(null);
  const [todayData, setTodayData]           = useState(null); // always today's stats, unaffected by month filter
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [leaderboard, setLeaderboard]       = useState([]);
  const [openReview, setOpenReview]         = useState(null); // { id, type }
  const [engModal, setEngModal]             = useState(false);
  const [chartMode, setChartMode]           = useState('daily');
  const defaultChartMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const [chartMonth, setChartMonth]         = useState(defaultChartMonth);
  const [monthTrend, setMonthTrend]         = useState([]);

  const [livePulse, setLivePulse]           = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [wishedIds, setWishedIds]           = useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const stored = JSON.parse(localStorage.getItem('glos_wished') || '{}');
      return new Set(stored.date === today ? (stored.keys || []) : []);
    } catch { return new Set(); }
  });
  const [wishingId, setWishingId]           = useState(null);

  useEffect(() => {
    const id = setInterval(() => setLivePulse(p => !p), 2500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setLoading(true);
    api.getMyDashboard(appId)
      .then(d => { setData(d); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to load dashboard');
        setLoading(false);
      });
  }, [appId]);

  useEffect(() => {
    api.getMyDashboard(appId)
      .then(d => setTodayData(d))
      .catch(() => {});
  }, [appId]);

  useEffect(() => {
    api.getUpcomingEvents(appId)
      .then(d => setUpcomingEvents(d.events || []))
      .catch(() => {});
  }, [appId]);

  useEffect(() => {
    api.getLeaderboard(appId)
      .then(d => setLeaderboard(d.members || []))
      .catch(() => {});
  }, [appId]);

  useEffect(() => {
    if (chartMode !== 'month') return;
    api.getMonthHours(appId, chartMonth)
      .then(d => setMonthTrend(d.trend || []))
      .catch(() => setMonthTrend([]));
  }, [appId, chartMode, chartMonth]);

  const [taskMonth, setTaskMonth]         = useState(defaultChartMonth);
  const [taskMonthData, setTaskMonthData] = useState(null);
  useEffect(() => {
    if (taskMonth === defaultChartMonth) { setTaskMonthData(null); return; }
    api.getMyDashboard(appId, taskMonth)
      .then(d => setTaskMonthData(d.tasks))
      .catch(() => {});
  }, [appId, taskMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWish = async (ev) => {
    const key = `${ev.user_id}-${ev.type}`;
    setWishingId(key);
    try {
      await api.sendWish(appId, ev.user_id, ev.type);
      setWishedIds(prev => {
        const next = new Set([...prev, key]);
        try {
          const today = new Date().toISOString().slice(0, 10);
          localStorage.setItem('glos_wished', JSON.stringify({ date: today, keys: [...next] }));
        } catch {}
        return next;
      });
    } catch {}
    setWishingId(null);
  };

  if (loading) return <div className="mdb-loading">Loading your dashboard…</div>;
  if (error)   return <div className="mdb-error">{error}</div>;
  if (!data)   return null;

  const {
    user, primary_role_name, hours, hours_sparklines, hours_deltas,
    daily_trend, weekly_trend, monthly_trend, yearly_trend,
    tasks, today_tasks,
    active_review_cycle,
    glos_score,
    recent_activity,
    pending_discussions,
    my_pending_reviews,
    active_complaint_tasks = 0,
    active_service_count   = 0,
  } = data;

  const chartData = chartMode === 'daily'   ? daily_trend
                  : chartMode === 'weekly'  ? weekly_trend
                  : chartMode === 'month'   ? monthTrend
                  : monthly_trend;
  const labelFn   = chartMode === 'daily'   ? d => fmtDate(d.day)
                  : chartMode === 'weekly'  ? d => fmtWeek(d.week)
                  : chartMode === 'month'   ? d => fmtDate(d.day)
                  : d => fmtMonth(d.month);

  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i <= 23; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      opts.push({ val, label });
    }
    return opts;
  })();

  const wowPct    = hours_deltas?.wow_pct ?? 0;
  const momPct    = hours_deltas?.mom_pct ?? 0;
  const yoyPct    = hours_deltas?.yoy_pct ?? 0;

  const eng      = glos_score || { total: 0, categories: [] };
  const engTarget = 200;
  const engLevel = eng.total >= 150 ? { label: 'Top Performer',  icon: '🏆', color: '#10b981' }
    : eng.total >= 100 ? { label: 'High Performer', icon: '🌟', color: '#3b82f6' }
    : eng.total >= 50  ? { label: 'Active',          icon: '⚡', color: '#f59e0b' }
    : eng.total >= 20  ? { label: 'Building',        icon: '📈', color: '#f97316' }
    :                    { label: 'Getting Started', icon: '🌱', color: '#9ca3af' };

  // Filter out past meetings and rejected reviews
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcomingMeetings = (pending_discussions || []).filter(d => {
    const meetDate = d.discussion_date ? String(d.discussion_date).slice(0, 10) : '';
    return meetDate >= todayStr && d.status !== 'rejected';
  });

  // Hide work anniversaries < 1 year from the list
  const visibleEvents = upcomingEvents.filter(ev => !(ev.type === 'work_anniversary' && ev.years === 0));

  // Today's Summary events count: only today's events that haven't been wished yet
  const summaryEventCount = visibleEvents.filter(ev => {
    if (ev.days_until !== 0) return false;
    if (me && ev.user_id === me.id) return false;
    return !wishedIds.has(`${ev.user_id}-${ev.type}`);
  }).length;

  // Birthday / joining / anniversary banners for logged-in user
  const isTodayMyBirthday = !!upcomingEvents.find(ev => ev.type === 'birthday' && ev.days_until === 0 && me && ev.user_id === me.id);
  const isTodayMyJoiningDay = !!upcomingEvents.find(ev => ev.type === 'work_anniversary' && ev.days_until === 0 && ev.years === 0 && me && ev.user_id === me.id);
  const myAnniversaryEvent = upcomingEvents.find(ev => ev.type === 'work_anniversary' && ev.days_until === 0 && ev.years >= 1 && me && ev.user_id === me.id);

  // Inline review view — clicking calendar date opens review here
  if (openReview) {
    const onBack = () => setOpenReview(null);
    if (openReview.type === 'daily')   return <DailyCheckinView reviewId={openReview.id} appId={appId} onBack={onBack} />;
    if (openReview.type === 'weekly')  return <WeeklyCheckinView reviewId={openReview.id} appId={appId} onBack={onBack} />;
    if (openReview.type === 'monthly') return <MonthlyReviewView reviewId={openReview.id} appId={appId} onBack={onBack} />;
  }

  return (
    <div className="mdb-page">

      {/* ── Banners ───────────────────────────────────────────── */}
      {isTodayMyBirthday && (
        <div className="mdb-birthday-banner">
          <span className="mdb-birthday-banner-emoji">🎂</span>
          <div className="mdb-birthday-banner-text">
            <strong>Happy Birthday, {firstName(user)}!</strong>
            <span> Wishing you a wonderful day filled with joy. 🎉</span>
          </div>
          <span className="mdb-birthday-banner-emoji">🎈</span>
        </div>
      )}
      {isTodayMyJoiningDay && (
        <div className="mdb-welcome-banner">
          <span className="mdb-welcome-banner-emoji">🎊</span>
          <div className="mdb-welcome-banner-text">
            <strong>Welcome to {app?.name || 'the team'}, {firstName(user)}!</strong>
            <span> We're so glad to have you here. Here's to an amazing journey ahead!</span>
          </div>
          <span className="mdb-welcome-banner-emoji">🙌</span>
        </div>
      )}
      {myAnniversaryEvent && (
        <div className="mdb-anniversary-banner">
          <span className="mdb-anniversary-banner-emoji">🏆</span>
          <div className="mdb-anniversary-banner-text">
            <strong>{app?.name || 'The team'} is wishing you a Happy Work Anniversary! 🎉</strong>
            <span> {myAnniversaryEvent.years} Year{myAnniversaryEvent.years > 1 ? 's' : ''} — keep shining!</span>
          </div>
        </div>
      )}

      {/* ── Top Row: (Hero + Perf Reviews) LEFT | Today's Summary RIGHT ── */}
      <div className="mdb-top-row">
        <div className="mdb-top-left-col">
          <section className="mdb-hero">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className="mdb-hero-avatar-col">
                <div className="mdb-hero-avatar">
                  {clerkUser?.imageUrl
                    ? <img src={clerkUser.imageUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : (
                      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
                        <circle cx="12" cy="8" r="4" fill="currentColor" opacity="0.7"/>
                        <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5"/>
                      </svg>
                    )}
                </div>
              </div>
              <div className="mdb-hero-text-col">
                <div className="mdb-hero-eyebrow">
                  <span className={`mdb-live-dot${livePulse ? ' pulse' : ''}`} />
                  {todayEyebrow()}
                </div>
                <h1 className="mdb-hero-h1">
                  Welcome, <span className="mdb-hero-name">{displayName(user)}</span>! 👋
                </h1>
                {primary_role_name && (
                  <div className="mdb-hero-role">👑 {primary_role_name}</div>
                )}
                <p className="mdb-hero-sub">Here's what's happening with your work today.</p>
              </div>
            </div>
          </section>

          {my_pending_reviews?.length > 0 ? (
            <div className="mdb-card mdb-perf-reviews-card">
              <div className="mdb-card-head">
                <h2 className="mdb-card-title">My Performance Reviews</h2>
                <Link to={`/apps/${appId}/performance?tab=mywork`} className="mdb-link-btn">→</Link>
              </div>
              <div className="mdb-perf-reviews-list">
                {my_pending_reviews.slice(0, 2).map(r => {
                  const stageLabel =
                    r.status === 'pending_self'    ? 'Self Review Pending' :
                    r.status === 'pending_manager' ? 'Manager Review Pending' :
                                                     'Approval Pending';
                  const stageColor =
                    r.status === 'pending_self'    ? '#f59e0b' :
                    r.status === 'pending_manager' ? '#3b82f6' :
                                                     '#8b5cf6';
                  const isActionRequired = r.status === 'pending_self';
                  return (
                    <div key={r.id} className="mdb-perf-review-row">
                      <div className="mdb-perf-review-icon">📋</div>
                      <div className="mdb-perf-review-body">
                        <div className="mdb-perf-review-name">{r.cycle_name}</div>
                        <div className="mdb-perf-review-meta">
                          <span>{r.periodicity} · {r.year}</span>
                        </div>
                      </div>
                      <div className="mdb-perf-review-right">
                        <span className="mdb-perf-stage-chip" style={{ background: stageColor + '1a', color: stageColor }}>
                          {stageLabel}
                        </span>
                        {isActionRequired && (
                          <Link to={`/apps/${appId}/performance?tab=mywork`} className="mdb-perf-action-btn">
                            Complete Now →
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mdb-card mdb-perf-reviews-card">
              <h2 className="mdb-card-title">My Performance Reviews</h2>
              <p className="mdb-empty-text">No pending performance reviews. Reviews will appear here when a cycle is active.</p>
            </div>
          )}

          {/* Upcoming Events — max 3, always shown */}
          <div className="mdb-card mdb-events-card">
            <div className="mdb-card-head">
              <h2 className="mdb-card-title">Upcoming Events</h2>
              <span className="mdb-events-window">Next 15 days</span>
            </div>
            {visibleEvents.length === 0 ? (
              <p className="mdb-empty-text">No upcoming birthdays or anniversaries in the next 15 days.</p>
            ) : (
              <ul className="mdb-events-list">
                {visibleEvents.map((ev, i) => {
                  const key = `${ev.user_id}-${ev.type}`;
                  const isBirthday = ev.type === 'birthday';
                  const icon = isBirthday ? '🎂' : '🎉';
                  const label = isBirthday ? 'Birthday' : `Work Anniversary${ev.years ? ` · ${ev.years}yr` : ''}`;
                  const dateStr = new Date(ev.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  const daysLabel = ev.days_until === 0 ? 'Today!' : ev.days_until === 1 ? 'Tomorrow' : `in ${ev.days_until} days`;
                  const wished = wishedIds.has(key);
                  const wishing = wishingId === key;
                  const isSelf = me && ev.user_id === me.id;
                  return (
                    <li key={i} className={`mdb-event-row${ev.days_until === 0 ? ' today' : ''}`}>
                      <span className="mdb-event-icon">{icon}</span>
                      <div className="mdb-event-info">
                        <span className="mdb-event-name">{ev.name}</span>
                        <span className="mdb-event-label">{label}</span>
                      </div>
                      <div className="mdb-event-right">
                        <span className="mdb-event-date">{dateStr}</span>
                        <span className={`mdb-event-days${ev.days_until === 0 ? ' today' : ''}`}>{daysLabel}</span>
                      </div>
                      {!isSelf && (
                        <button
                          className={`mdb-wish-btn${wished ? ' wished' : ''}`}
                          onClick={() => !wished && ev.days_until === 0 && handleWish(ev)}
                          disabled={wishing || wished || ev.days_until !== 0}
                          title={ev.days_until !== 0 ? `Available on ${dateStr}` : undefined}
                        >
                          {wished ? '✓ Wished' : wishing ? '…' : '🎁 Wish'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Hours Logged Trend — fills remaining space below events */}
          <div className="mdb-card mdb-card-chart">
            <div className="mdb-card-head">
              <h2 className="mdb-card-title">Hours Logged Trend</h2>
              <div className="mdb-chart-tabs">
                {[
                  { key: 'daily',   label: 'Last 14 days' },
                  { key: 'weekly',  label: '8 weeks' },
                  { key: 'monthly', label: '12 months' },
                ].map(({ key, label }) => (
                  <button key={key}
                    className={`mdb-chart-tab${chartMode === key ? ' active' : ''}`}
                    onClick={() => setChartMode(key)}>
                    {label}
                  </button>
                ))}
                {/* Month tab: click = current month; arrow = pick other month */}
                <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <button
                    className={`mdb-chart-tab${chartMode === 'month' ? ' active' : ''}`}
                    style={{ borderRadius: '6px 0 0 6px', borderRight: 'none' }}
                    onClick={() => { setChartMonth(defaultChartMonth); setChartMode('month'); }}
                  >
                    {chartMode === 'month' && chartMonth !== defaultChartMonth
                      ? monthOptions.find(o => o.val === chartMonth)?.label?.replace(/\s\d{4}$/, '') || 'Month'
                      : 'This Month'}
                  </button>
                  <MonthPickerArrow
                    value={chartMonth}
                    active={chartMode === 'month'}
                    options={monthOptions.map(o => ({ val: o.val, lbl: o.label }))}
                    onChange={val => { setChartMonth(val); setChartMode('month'); }}
                    buttonStyle={{ borderRadius: '0 6px 6px 0' }}
                  />
                </div>
              </div>
            </div>
            {chartData?.some(d => d.minutes > 0)
              ? <HoursAreaChart data={chartData} labelFn={labelFn} />
              : <div className="mdb-chart-empty">No hours logged in this period.</div>
            }
          </div>

          {/* ── My Projects ───────────────────────────────── */}
          <MyProjectsSection appId={appId} me={me} />

          {/* ── Routine Summary ──────────────────────────── */}
          <MyRoutineSummary appId={appId} api={api} />

        </div>

        {/* Right column: Today's Summary + Calendar + Leaderboard stacked */}
        <div className="mdb-top-right-col">
        <div className="mdb-card mdb-today-summary">
          <h2 className="mdb-today-summary-title">Today's Summary</h2>
          {(() => {
            const td = todayData || data;   // today-only fields: completed_today, due_today, hours today
            const t  = td.tasks;
            const h  = td.hours;
            return (
              <div className="mdb-today-summary-grid">
                <div className="mdb-summary-tile">
                  <div className="mdb-summary-tile-icon-wrap" style={{ background: '#d1fae5' }}>✅</div>
                  <div className="mdb-summary-tile-body">
                    <div className="mdb-summary-tile-label">Today's Tasks</div>
                    <div className="mdb-summary-tile-val">{t.completed_today ?? 0}</div>
                    <div className="mdb-summary-tile-sub">Completed today</div>
                  </div>
                </div>
                <div className="mdb-summary-tile">
                  <div className="mdb-summary-tile-icon-wrap" style={{ background: 'rgb(195 195 195 / 19%)' }}>⏱</div>
                  <div className="mdb-summary-tile-body">
                    <div className="mdb-summary-tile-label">Hours Logged</div>
                    <div className="mdb-summary-tile-val" style={{ fontSize: 15 }}>
                      {fmtHoursMins(h.today.minutes)}
                      <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: 13 }}>
                        {' '}/ {fmtHoursMins(t.today_planned_minutes)}
                      </span>
                    </div>
                    <div className="mdb-summary-tile-sub">
                      {h.today.minutes >= t.today_planned_minutes && t.today_planned_minutes > 0 ? 'On track' : 'Planned today'}
                    </div>
                  </div>
                </div>
                <div className="mdb-summary-tile">
                  <div className="mdb-summary-tile-icon-wrap" style={{ background: '#fee2e2' }}>⚠️</div>
                  <div className="mdb-summary-tile-body">
                    <div className="mdb-summary-tile-label">Due Today</div>
                    <div className="mdb-summary-tile-val" style={{ color: t.due_today > 0 ? '#ef4444' : undefined }}>{t.due_today || 0}</div>
                    <div className="mdb-summary-tile-sub">
                      {t.due_today > 0 ? 'Need attention' : 'Nothing due today'}
                    </div>
                  </div>
                </div>
                <div className="mdb-summary-tile">
                  <div className="mdb-summary-tile-icon-wrap" style={{ background: '#d1fae5' }}>✅</div>
                  <div className="mdb-summary-tile-body">
                    <div className="mdb-summary-tile-label">On-Time Rate</div>
                    <div className="mdb-summary-tile-val" style={{ color: t.today_on_time_pct != null ? (t.today_on_time_pct >= 80 ? '#10b981' : t.today_on_time_pct >= 50 ? '#f59e0b' : '#ef4444') : undefined }}>
                      {t.today_on_time_pct != null ? `${t.today_on_time_pct}%` : '—'}
                    </div>
                    <div className="mdb-summary-tile-sub">Completed on time today</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

          {/* Queries (for me / asked by me) | Business Review Calendar */}
          <QueriesPanel
            appId={appId}
            scopes={['for_me', 'asked']}
            className="mdb-card mdb-calrev-card"
            headClassName="mdb-card-head"
            titleClassName="mdb-card-title"
            title="Business Review Calendar"
            calendarLegend={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="br-cal-legend">
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', marginRight: 4 }} />
                  Open
                </span>
                <span className="br-cal-legend">
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', marginRight: 4 }} />
                  Completed
                </span>
              </div>
            }
            calendar={<DailyCalendarWidget appId={appId} onOpenReview={setOpenReview} />}
          />

          {/* GLOS Leaderboard */}
          <div className="mdb-leaderboard-card">
            {(() => {
              const myId = data?.user?.id;
              const myIdx = leaderboard.findIndex(m => String(m.user?.id) === String(myId));
              const myRank = myIdx + 1;
              const myScore = myIdx >= 0 ? (leaderboard[myIdx].glos_score?.total || 0) : (eng.total || 0);
              return (
                <>
                  <div className="mdb-lb-header">
                    <div className="mdb-lb-header-top">
                      <span className="mdb-lb-trophy">🏆</span>
                      <div className="mdb-lb-title-block">
                        <h2 className="mdb-lb-title">GLOS LEADERBOARD</h2>
                        {myRank > 0 && (
                          <span className="mdb-lb-my-rank">RANK <span className="mdb-lb-rank-num">#{myRank}</span> · <span className="mdb-lb-rank-pts">{myScore} pts</span></span>
                        )}
                      </div>
                    </div>
                    <button className="mdb-lb-view-score-btn" onClick={() => setEngModal(true)}>
                      <span>📊</span> View my score →
                    </button>
                  </div>
                  {!leaderboard.length ? (
                    <p className="mdb-empty-text" style={{ padding: '16px 20px' }}>No team data yet.</p>
                  ) : (
                    <div className="mdb-lb-table">
                      <div className="mdb-lb-thead">
                        <span className="mdb-lb-col-rank">RANK</span>
                        <span className="mdb-lb-col-user">USER</span>
                        <span className="mdb-lb-col-pts">POINTS</span>
                      </div>
                      <div className="mdb-lb-tbody">
                        {leaderboard.map((m, i) => {
                          const isMe = String(m.user?.id) === String(myId);
                          const score = m.glos_score?.total || 0;
                          const rank = i + 1;
                          const zone = rank === 1 ? 'r1' : rank <= 3 ? 'top3' : rank <= 5 ? 'mid' : 'rest';
                          const name = [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ') || m.user?.email || `User ${m.user?.id}`;
                          return (
                            <div key={m.user?.id || i} className={`mdb-lb-row mdb-lb-zone-${zone}${isMe ? ' mdb-lb-me' : ''}`}>
                              <span className="mdb-lb-col-rank">
                                <span className={`mdb-lb-rank-circle mdb-lb-rank-${zone}`}>{rank}</span>
                              </span>
                              <span className="mdb-lb-col-user">
                                <span className="mdb-lb-name">{isMe ? 'YOU' : name}</span>
                              </span>
                              <span className={`mdb-lb-col-pts mdb-lb-pts-val mdb-lb-pts-${zone}`}>
                                {score} {score === 1 ? 'pt' : 'pts'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* ── Task Completion ─────────────────────────────── */}
          <div className="mdb-card mdb-card-tasks">
            <div className="mdb-card-head">
              <h2 className="mdb-card-title">Task Completion</h2>
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <button
                  className={`mdb-chart-tab${taskMonth !== defaultChartMonth ? ' active' : ''}`}
                  style={{ borderRadius: '6px 0 0 6px', borderRight: 'none' }}
                  onClick={() => setTaskMonth(defaultChartMonth)}
                >
                  {taskMonth !== defaultChartMonth
                    ? monthOptions.find(o => o.val === taskMonth)?.label?.replace(/\s\d{4}$/, '') || 'Month'
                    : 'This Month'}
                </button>
                <MonthPickerArrow
                  value={taskMonth}
                  active={taskMonth !== defaultChartMonth}
                  options={monthOptions.map(o => ({ val: o.val, lbl: o.label }))}
                  onChange={setTaskMonth}
                  buttonStyle={{ borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
            <TaskDonut tasks={taskMonthData || tasks} />
          </div>

        </div>{/* end mdb-top-right-col */}
      </div>{/* end mdb-top-row */}

      {/* ── Stat Row removed ──────────────────────────────────── */}
      {false && <section className="mdb-stat-row">
        <div className="mdb-stat">
          <div className="mdb-stat-label">
            <span>Today</span>
            <span className="mdb-stat-sub">{fmtDate(new Date().toISOString())}</span>
          </div>
          <div className="mdb-stat-value">
            {fmtHours(hours.today.minutes)}<span className="mdb-unit">h</span>
          </div>
          <div className="mdb-stat-meta">
            <span className="mdb-chip pos">on track</span>
            <span className="mdb-stat-target">Target 8h</span>
          </div>
          <Sparkline data={hours_sparklines} />
        </div>

        <div className="mdb-stat">
          <div className="mdb-stat-label">
            <span>This Week</span>
            <span className="mdb-stat-sub">Last 7 days</span>
          </div>
          <div className="mdb-stat-value">
            {fmtHours(hours.this_week.minutes)}<span className="mdb-unit">h</span>
          </div>
          <div className="mdb-stat-meta">
            <DeltaChip pct={wowPct} label="vs last week" />
          </div>
          <Sparkline data={hours_sparklines} />
        </div>

        <div className="mdb-stat">
          <div className="mdb-stat-label">
            <span>This Month</span>
            <span className="mdb-stat-sub">
              {new Date().toLocaleDateString(undefined, { month: 'long' })}
            </span>
          </div>
          <div className="mdb-stat-value">
            {fmtHours(hours.this_month.minutes)}<span className="mdb-unit">h</span>
          </div>
          <div className="mdb-stat-meta">
            <DeltaChip pct={momPct} label="vs last month" />
          </div>
          <Sparkline data={(monthly_trend || []).slice(-7)} />
        </div>

        <div className="mdb-stat">
          <div className="mdb-stat-label">
            <span>This Year</span>
            <span className="mdb-stat-sub">{new Date().getFullYear()}</span>
          </div>
          <div className="mdb-stat-value">
            {fmtHours(hours.this_year.minutes)}<span className="mdb-unit">h</span>
          </div>
          <div className="mdb-stat-meta">
            <DeltaChip pct={yoyPct} label="vs last year" />
          </div>
          <Sparkline data={(yearly_trend || []).slice(-7)} />
        </div>
      </section>}


      {engModal && (
        <MyCriteriaModal categories={eng.categories} total={eng.total} level={engLevel} onClose={() => setEngModal(false)} />
      )}

    </div>
  );
}
